import { useFormikWithDirtyForm } from "@shared";
import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { subDays } from "date-fns";

import { AppointmentResponse, DeleteAppointmentRequest, UpdateAppointmentRequest, appointmentsApi } from "@/api";
import {
  AppointmentClientSelection,
  AppointmentFormValues,
} from "@/common/components/appointments/shared/AppointmentForm.types";
import {
  deriveAvailability,
  toAllDayEnd,
  toAllDayStart,
} from "@/common/components/appointments/shared/AppointmentForm.utils";
import { useAppointmentFormOptions } from "@/common/components/appointments/shared/hooks/useAppointmentFormOptions";
import { nameof } from "@/common/helper/commonHelper";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useAppointmentEdit(appointment: AppointmentResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();
  const { validationSchema, ...fieldOptions } = useAppointmentFormOptions();

  const assignedClient: AppointmentClientSelection | null = appointment.clientId
    ? {
        clientId: appointment.clientId,
        name: `${appointment.clientLastName}, ${appointment.clientFirstName}`,
        phone: appointment.clientPhone ?? null,
        email: appointment.clientEmail ?? null,
      }
    : null;

  const updateAppointmentMutation = useMutation({
    mutationFn: (request: UpdateAppointmentRequest) =>
      appointmentsApi.updateAppointment({
        appointmentId: appointment.appointmentId,
        updateAppointmentRequest: request,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
      showSuccess(t("calendar.appointmentEdit.successMessage"));
      onClose();
    },
  });

  const deleteAppointmentMutation = useMutation({
    mutationFn: (request?: DeleteAppointmentRequest) =>
      appointmentsApi.deleteAppointment({
        appointmentId: appointment.appointmentId,
        deleteAppointmentRequest: request,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
      showSuccess(t("calendar.appointmentEdit.deleteSuccessMessage"));
      onClose();
    },
  });

  const buildUpdateRequest = (values: AppointmentFormValues): UpdateAppointmentRequest => {
    const availability = deriveAvailability(values.purposeKey, fieldOptions.purposes);
    const clientInvolved = !!assignedClient || !!values.client;
    const notifyBySms = clientInvolved && values.notifyBySms;
    const notifyByEmail = clientInvolved && values.notifyByEmail;

    return {
      purposeKey: values.purposeKey,
      availability,
      startTimeUtc: values.startDate,
      endTimeUtc: values.endDate,
      isAllDay: values.isAllDay,
      originTimeZoneId: values.isAllDay || notifyBySms || notifyByEmail ? getLocalTimeZoneId() : undefined,
      location: values.location,
      details: values.details || null,
      // The backend treats a null clientId as removal, so the current assignment must be
      // echoed on every unrelated edit.
      clientId: values.clientMarkedForRemoval ? null : (assignedClient?.clientId ?? values.client?.clientId ?? null),
      notifyBySms,
      notifyByEmail,
    };
  };

  const formik = useFormikWithDirtyForm<AppointmentFormValues>({
    initialValues: {
      isAllDay: appointment.isAllDay,
      startDate: appointment.isAllDay ? toAllDayStart(appointment.startTimeUtc) : appointment.startTimeUtc,
      endDate: appointment.isAllDay ? toAllDayEnd(subDays(appointment.endTimeUtc, 1)) : appointment.endTimeUtc,
      purposeKey: appointment.purposeKey,
      location: appointment.location ?? null,
      details: appointment.details ?? "",
      client: null,
      // SMS is the default channel, but only when the assigned client has a phone number.
      notifyBySms: !!assignedClient?.phone,
      notifyByEmail: false,
      clientMarkedForRemoval: false,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      // Awaited, so the form stays submitting until the confirmed save is done.
      if (values.clientMarkedForRemoval && assignedClient) {
        const confirmed = await new Promise<boolean>((resolve) => {
          showConfirmationDialog({
            dialogTitleText: t("calendar.appointmentClient.removeConfirmTitle"),
            dialogText: t("calendar.appointmentClient.removeConfirmText", { name: assignedClient.name }),
            discardText: t("common.cancel"),
            agreeText: t("calendar.appointmentClient.removeConfirmAgree"),
            onClose: resolve,
          });
        });

        if (!confirmed) {
          // Declined: nothing is saved, the dialog stays open with the marking intact.
          return;
        }
      }

      try {
        await updateAppointmentMutation.mutateAsync(buildUpdateRequest(values));
      } catch {
        // A rejected save (e.g. a failed notification aborting the update) is reported by the
        // global error handling; the form is released so the user can adjust and retry.
      }
    },
  });

  const handleIsAllDayChange = (checked: boolean) => {
    void formik.setFieldValue(nameof<AppointmentFormValues>("isAllDay"), checked);

    if (checked) {
      void formik.setFieldValue(nameof<AppointmentFormValues>("startDate"), toAllDayStart(formik.values.startDate));
      void formik.setFieldValue(nameof<AppointmentFormValues>("endDate"), toAllDayEnd(formik.values.endDate));
    }
  };

  const handleDeleteClick = () => {
    showConfirmationDialog({
      dialogTitleText: t("calendar.appointmentEdit.deleteConfirmTitle"),
      dialogText: assignedClient
        ? t("calendar.appointmentEdit.deleteConfirmTextWithClient", { name: assignedClient.name })
        : t("calendar.appointmentEdit.deleteConfirmText"),
      discardText: t("common.cancel"),
      agreeText: t("common.delete"),
      agreeColor: "error",
      onClose: (confirmed: boolean) => {
        if (confirmed) {
          // Deleting counts as a cancellation: the client is notified via the channels
          // currently checked in the edit dialog behind this confirmation.
          deleteAppointmentMutation.mutate(
            assignedClient
              ? {
                  reason: null,
                  notifyBySms: formik.values.notifyBySms,
                  notifyByEmail: formik.values.notifyByEmail,
                  originTimeZoneId: getLocalTimeZoneId(),
                }
              : undefined
          );
        }
      },
    });
  };

  return {
    formik,
    fieldOptions,
    assignedClient,
    handleIsAllDayChange,
    handleDeleteClick,
    isDeleting: deleteAppointmentMutation.isPending,
  };
}
